import { useNavigate } from "react-router-dom-v5-compat";
import {
  CarsRawEventName,
  CoreCarsComponent,
  DropoffType,
  useEmitApacCarsRawEventOnce,
  type CarSearchComponentProps,
} from "@apac/components/cars";
import {
  BestPriceGuaranteeLink,
  LocationAutocompleteTrigger,
} from "@apac/components/ui";
import { useBooleanFlag } from "@apac/feature-flags";
import { ControlledTextField } from "@apac/form-ui";
import { useModuleBEM, IconNameEnum } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { noop } from "@hopper-b2b/utilities";
import {
  CARS_SEARCH_DROP_OFF_PATH,
  CARS_SEARCH_PICKUP_PATH,
  FeatureFlagNames,
} from "@hsbc/utilities";
import { CarDateRangePicker, type CarLocationSuggestion } from "@tenant/cars";
import {
  Button,
  ButtonTypeEnum,
  Icon,
  Checkbox,
  TimePicker,
  CloseButton,
  DialogTitle,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./styles.module.scss";
import { useCarSearch } from "./useCarSearch";

export const CarSearch = ({
  children,
  className,
  onSubmit,
}: CarSearchComponentProps) => {
  const [block, cn] = useModuleBEM(styles, CoreCarsComponent.CarSearch);

  const { t } = useI18nContext();
  const navigate = useNavigate();

  const {
    context: {
      pickupTime,
      dropoffTime,
      pickupDate,
      pickupLocation,
      dropoffDate,
      dropoffLocation,
      driverAge,
      isFormValid,
      dropoffType,
      enableBPG,
    },
    handlers: {
      handleDriverAgeChange,
      handlePickupDateChange,
      handleDropoffDateChange,
      handlePickupTimeChange,
      handleDropoffTimeChange,
      onFormSubmit,
      handleDropoffTypeChange,
    },
  } = useCarSearch(onSubmit);

  const sameLocationDropoff = dropoffType === DropoffType.RoundTrip;

  useEmitApacCarsRawEventOnce({ name: CarsRawEventName.CarSearchLoaded });

  const calendarHeader = () => (
    <div className={cn("calendar-header")}>
      <CloseButton label={t("back")} className={cn("back-button")}>
        <Icon
          className={cn("back-button-icon")}
          iconName={IconNameEnum.backIcon}
        />
      </CloseButton>
      <div className={cn("header-content")}>
        <DialogTitle className={cn("header-title")}>
          {t("cars.dateSelection.title")}
        </DialogTitle>
        <div className={cn("header-location")}>
          <span className={cn("header-location-origin")}>
            {pickupLocation?.label}
          </span>
          <Icon
            className={cn("header-product-icon")}
            iconName={IconNameEnum.car}
          />
          <span className={cn("header-location-destination")}>
            {dropoffLocation?.label}
          </span>
        </div>
        {driverAge != null && (
          <div className={cn("header-passenger")}>
            {t("cars.age", { age: driverAge })}
          </div>
        )}
      </div>
      <div className={cn("right-button")} />
    </div>
  );

  return (
    <form className={clsx(block, className)} onSubmit={onFormSubmit}>
      <div className={cn("location-button", ["pickup"])}>
        <span className={cn("label")}>{t("core-cars.search.pickup")}</span>
        <Button
          buttonType={ButtonTypeEnum.Wrap}
          onClick={() => navigate(CARS_SEARCH_PICKUP_PATH)}
          aria-label={t("core-cars.search.pickup")}
        >
          <LocationAutocompleteTrigger
            label={t("core-cars.search.pickup")}
            value={pickupLocation as CarLocationSuggestion}
          />
        </Button>
      </div>

      <div className={cn("dropoff-type")}>
        <Checkbox
          checked={!sameLocationDropoff}
          onCheckedChange={() =>
            handleDropoffTypeChange(
              sameLocationDropoff ? DropoffType.OneWay : DropoffType.RoundTrip
            )
          }
          aria-label={t("core-cars.search.differentLocationLabel")}
        />
        <span>{t("core-cars.search.differentLocationLabel")}</span>
      </div>

      <div
        className={cn("location-button", {
          disabled: sameLocationDropoff,
          dropoff: true,
        })}
      >
        <span className={cn("label")}>{t("core-cars.search.dropoff")}</span>
        <Button
          buttonType={ButtonTypeEnum.Wrap}
          onClick={() => navigate(CARS_SEARCH_DROP_OFF_PATH)}
          aria-label={t("core-cars.search.dropoff")}
        >
          <LocationAutocompleteTrigger
            label={t("core-cars.search.dropoff")}
            value={
              sameLocationDropoff
                ? undefined
                : (dropoffLocation as CarLocationSuggestion)
            }
          />
        </Button>
      </div>

      <div className={cn("date-range")}>
        <div className={cn("date-range-labels")}>
          <label className={clsx(cn("label"), cn("date-range-label-item"))}>
            {t("cars.pickupDate")}
          </label>
          <label className={clsx(cn("label"), cn("date-range-label-item"))}>
            {t("cars.dropoffDate")}
          </label>
        </div>
        <CarDateRangePicker
          startDate={pickupDate}
          endDate={dropoffDate}
          onStartDateChangeHandler={handlePickupDateChange}
          onEndDateChangeHandler={handleDropoffDateChange}
          header={calendarHeader()}
          ariaLabel={t("cars.dateSelection.title")}
        />
      </div>

      <div className={cn("time-range")}>
        <div className={cn("time-picker-wrapper")}>
          <label className={cn("label")} htmlFor="pickup-time">
            {t("core-cars.search.pickupTime")}
          </label>
          <TimePicker
            placeholder={t("select")}
            value={pickupTime}
            onValueChange={handlePickupTimeChange}
            onChange={noop}
            name="pickup-time"
            className={cn("time-picker", { hasValue: pickupTime != null })}
            label={t("core-cars.search.pickupTime")}
          />
        </div>

        <div className={cn("time-picker-wrapper")}>
          <label className={cn("label")} htmlFor="dropoff-time">
            {t("core-cars.search.dropoffTime")}
          </label>
          <TimePicker
            value={dropoffTime}
            placeholder={t("select")}
            onValueChange={handleDropoffTimeChange}
            name="dropoff-time"
            onChange={noop}
            className={cn("time-picker", { hasValue: dropoffTime != null })}
            label={t("core-cars.search.dropoffTime")}
          />
        </div>
      </div>

      <div className={cn("driver-age")}>
        <label className={cn("label")} htmlFor="driver-age">
          {t("core-cars.search.driverAge")}
        </label>
        <ControlledTextField
          value={driverAge?.toString()}
          placeholder={t("apac.cars.driverAgePlaceholder")}
          name="driver-age"
          onChange={handleDriverAgeChange}
          type="number"
          onBlur={noop}
          aria-label={t("core-cars.search.driverAge")}
          icon={
            <Icon
              className={cn("driver-age-icon")}
              iconName={IconNameEnum.guest}
              useCurrentColor
            />
          }
        />
      </div>
      <Button
        className={cn("search-button")}
        buttonType={ButtonTypeEnum.Primary}
        type="submit"
        disabled={!isFormValid}
        aria-label={t("core-cars.search.searchButton")}
      >
        {t("core-cars.search.searchButton")}
      </Button>
      {children}

      {enableBPG === true && (
        <BestPriceGuaranteeLink iconName={IconNameEnum.cashBag} />
      )}
    </form>
  );
};
